import { useAppConfigStore } from "@/shared/stores/appConfig.store";
import { useThemeStore } from "@/shared/stores/theme.store";
import { useViewportStore } from "@/shared/stores/viewport.store";
import { HStack, Icon, VStack } from "@/shared/ui/primitives";
import { en, ko } from "@blocknote/core/locales";
import { useCreateBlockNote } from "@blocknote/react";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { workflowIdRoute } from "../route";
import "@blocknote/mantine/style.css";
import "@/shared/styles/blocknote.css";
import {
	useAutomationAgent,
	useAutomationChatHistory,
	useAutomationChatLog,
} from "@/features/agent";
import { useGetWorkflow, useUpdateWorkflow } from "@/features/workflow";
import koLocale from "@/locales/ko.json";
import { ConfirmModal } from "@/shared/ui/composites";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UpdateAssistantPanel } from "./components/UpdateAssistantPanel";
import { UpdatePrdEditorPanel } from "./components/UpdatePrdEditorPanel";
import {
	type ChatMessage,
	ensureWelcomeMessage,
	getSessionStorageKey,
	mapChatHistoryItems,
} from "./update.utils";

const LEGACY_ALPHA_WELCOME_MESSAGE = koLocale.workflow.alphaWelcomeMessage;

const Desktop = () => {
	const { t, i18n } = useTranslation();
	const alphaWelcomeMessage = t("workflow.alphaWelcomeMessage");
	const { isFirstTime, setIsFirstTime } = useAppConfigStore();
	const { theme } = useThemeStore();
	const isMobile = useViewportStore((s) => s.isMobile);
	const [activeTab, setActiveTab] = useState<"editor" | "assistant">("editor");
	const { id } = workflowIdUpdateRoute.useParams();
	const navigate = useNavigate();
	const { data: workflow } = useGetWorkflow(id);
	const updateWorkflow = useUpdateWorkflow();
	const { startStream, continueStream, isStreaming, events } = useAutomationAgent();
	const chatLog = useAutomationChatLog();
	const { data: chatHistory } = useAutomationChatHistory(id, 50);

	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [input, setInput] = useState("");
	const [alphaSessionId, setAlphaSessionId] = useState<string | null>(null);
	const [currentAssistantMessage, setCurrentAssistantMessage] = useState("");
	const [processedEventCount, setProcessedEventCount] = useState(0);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const storedSessionId = window.localStorage.getItem(getSessionStorageKey(id));
		setAlphaSessionId(storedSessionId);
	}, [id]);

	const editor = useCreateBlockNote({
		dictionary: {
			...(i18n.language.startsWith("en") ? en : ko),
			placeholders: {
				...(i18n.language.startsWith("en") ? en.placeholders : ko.placeholders),
				default: t("workflow.editorPlaceholder"),
			},
		},
	});

	// Load PRD into editor
	useEffect(() => {
		if (workflow?.prd && editor) {
			const blocks = editor.tryParseMarkdownToBlocks(workflow.prd);
			editor.replaceBlocks(editor.document, blocks);
		}
	}, [workflow?.prd, editor]);

	// Load chat history
	useEffect(() => {
		if (chatHistory?.items) {
			setMessages(
				ensureWelcomeMessage(
					mapChatHistoryItems(chatHistory.items),
					alphaWelcomeMessage,
					LEGACY_ALPHA_WELCOME_MESSAGE,
				),
			);
		}
	}, [alphaWelcomeMessage, chatHistory]);

	// Auto scroll on messages update
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages, currentAssistantMessage, isStreaming]);

	useEffect(() => {
		const el = textareaRef.current;
		if (!el) return;
		el.style.height = "auto";
		el.style.height = `${el.scrollHeight}px`;
	}, [input]);

	// Handle SSE events
	useEffect(() => {
		const newEvents = events.slice(processedEventCount);

		for (const event of newEvents) {
			if (event.event === "chunk") {
				setCurrentAssistantMessage((prev) => prev + event.content);
			} else if (event.event === "done") {
				if (event.sessionId) {
					setAlphaSessionId(event.sessionId);
					if (typeof window !== "undefined") {
						window.localStorage.setItem(getSessionStorageKey(id), event.sessionId);
					}
				} else if (event.status === "ERROR" && event.error?.code === "SESSION_EXPIRED") {
					setAlphaSessionId(null);
					if (typeof window !== "undefined") {
						window.localStorage.removeItem(getSessionStorageKey(id));
					}
				}
				const fullMessage =
					event.status === "ERROR"
						? (event.error?.message ??
							event.assistant_message ??
							event.message ??
							t("workflow.requestErrorFallback"))
						: event.assistant_message || event.message || "";
				const prd = event.spec_markdown;

				if (fullMessage) {
					setMessages((prev) => [...prev, { role: "assistant", content: fullMessage }]);
				}
				setCurrentAssistantMessage("");

				if (event.status !== "ERROR" && prd && editor) {
					const blocks = editor.tryParseMarkdownToBlocks(prd);
					editor.replaceBlocks(editor.document, blocks);
				}

				if (fullMessage) {
					chatLog.mutate({ workflowId: id, chat: fullMessage, type: "EDIT_REQUEST" });
				}
			} else if (event.event === "error") {
				setCurrentAssistantMessage("");
				setMessages((prev) => [
					...prev,
					{ role: "assistant", content: t("workflow.streamErrorPrefix", { error: event.error }) },
				]);
			}
		}

		setProcessedEventCount(events.length);
	}, [events, processedEventCount, editor, id, chatLog, t]);

	const handleSend = () => {
		const trimmed = input.trim();
		if (!trimmed || isStreaming) return;

		const userMessage: ChatMessage = { role: "user", content: trimmed };
		setMessages((prev) => [...prev, userMessage]);
		setInput("");

		// Start SSE streaming
		if (alphaSessionId) {
			continueStream({
				workflowId: id,
				sessionId: alphaSessionId,
				message: trimmed,
			});
		} else {
			startStream({
				workflowId: id,
				message: trimmed,
			});
		}
	};

	const handleSavePrd = async () => {
		const markdown = await editor.blocksToMarkdownLossy(editor.document);
		updateWorkflow.mutate(
			{ id, request: { prd: markdown } },
			{
				onSuccess: () => {
					navigate({ to: "/workflow/$id", params: { id } });
				},
			},
		);
	};

	return (
		<HStack className={`w-full h-full bg-bg-weak ${isMobile ? "p-[12px] flex-col" : "p-[20px] gap-[20px]"}`}>
			<ConfirmModal
				title={t("common.notSupportedTitle")}
				description={t("common.notSupportedDescription")}
				isOpen={isConfirmModalOpen}
				isCancelDisabled
				onConfirm={() => setIsConfirmModalOpen(false)}
				onCancel={() => setIsConfirmModalOpen(false)}
			/>
			<ConfirmModal
				title={t("workflow.firstTimeModalTitle")}
				description={t("workflow.firstTimeModalDescription")}
				isOpen={isFirstTime}
				isCancelDisabled
				onConfirm={() => setIsFirstTime(false)}
				onCancel={() => setIsFirstTime(false)}
			/>
			<div className={`${isMobile && activeTab !== "editor" ? "hidden" : "flex"} w-full h-full min-h-0 ${isMobile ? "pb-[60px]" : ""}`}>
				<UpdatePrdEditorPanel
					editor={editor}
					theme={theme}
					isSaving={updateWorkflow.isPending}
					isStreaming={isStreaming}
					saveLabel={t("common.save")}
					generateLabel={t("common.generate")}
					onSave={handleSavePrd}
					onGenerate={() => setIsConfirmModalOpen(true)}
				/>
			</div>
			<div className={`${isMobile && activeTab !== "assistant" ? "hidden" : "flex"} w-full h-full min-h-0 ${isMobile ? "pb-[60px]" : ""}`}>
				<UpdateAssistantPanel
					messages={messages}
					currentAssistantMessage={currentAssistantMessage}
					isStreaming={isStreaming}
					input={input}
					inputPlaceholder={t("workflow.inputUpdate")}
					onInputChange={setInput}
					onSend={handleSend}
					textareaRef={textareaRef}
					messagesEndRef={messagesEndRef}
				/>
			</div>
			{isMobile && (
				<HStack className="fixed bottom-0 left-0 right-0 z-80 bg-bg-weak/95 backdrop-blur-md border-t border-outline">
					<button
						className={`flex-1 py-[14px] text-sm font-medium ${activeTab === "editor" ? "text-pm-strong" : "text-ct-regular"}`}
						onClick={() => setActiveTab("editor")}
					>
						<VStack className="gap-[4px]">
							<Icon name="Action" width={20} height={20} />
							<span>{t("workflow.tabEditor")}</span>
						</VStack>
					</button>
					<button
						className={`flex-1 py-[14px] text-sm font-medium ${activeTab === "assistant" ? "text-pm-strong" : "text-ct-regular"}`}
						onClick={() => setActiveTab("assistant")}
					>
						<VStack className="gap-[4px]">
							<Icon name="Robot" width={20} height={20} />
							<span>{t("workflow.tabAssistant")}</span>
						</VStack>
					</button>
				</HStack>
			)}
		</HStack>
	);
};

export const workflowIdUpdateRoute = createRoute({
	getParentRoute: () => workflowIdRoute,
	path: "/update",
	component: Desktop,
});
